// ============================================
// notificationService.ts - Direct OneSignal integration
// ============================================

// ============ EMAILJS IMPORT (NEW) ============
import emailjs from '@emailjs/browser';

export interface NotificationRequest {
  title: string;
  body: string;
  target_user_id?: string;        // Single user
  target_user_ids?: string[];      // Multiple users
  email?: string;                  // Single email
  email_list?: string[];           // Multiple emails
  notification_type?: 'order' | 'chat' | 'system' | 'vendor' | 'promotion';
  redirect_url?: string;
  data?: Record<string, any>;
  imageUrl?: string;
}

export interface PushResult {
  success: boolean;
  count: number;
  error?: string;
  response?: any;
}

export interface EmailResult {
  success: boolean;
  count: number;
  error?: string;
}

export interface NotificationResponse {
  success: boolean;
  message: string;
  channels: string[];
  notificationId?: string;
  targetUsers?: number;
  emails?: number;
  error?: string;
  timestamp: string;
  results?: {
    push?: PushResult;
    email?: EmailResult;
  };
}

// ============ EMAILJS ACCOUNT SHAPE (NEW) ============
interface EmailJsAccount {
  serviceId: string;
  templateId: string;
  publicKey: string;
  initialized: boolean;
  label: string;
}

class NotificationService {
  private static instance: NotificationService;
  private backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000' // Your backend URL for emails only
  
  // OneSignal direct configuration
  private onesignalAppId = '8871d2ef-7cda-486e-b53b-53964c531d49';
  private onesignalApiKey = 'os_v2_app_rby5f3343jeg5nj3koleyuy5jhakz2afmpausi5ukse5f6sckn5c3x7runtpvrxa27hmgyhbpo2mvryd2lx2qwgskpmpkteigrgsvbq';

  // ============ EMAILJS CONFIG (NEW) ============
  // Multiple accounts — one is randomly chosen per send request.
  // If the chosen one fails entirely, we try the next one as fallback.
  private emailjsAccounts: EmailJsAccount[] = [
    {
      serviceId: 'service_28s246g',
      templateId: 'template_pfgp3te',
      publicKey: '8aA0otiM0u1nUtf7q',
      initialized: false,
      label: 'account-1'
    },
    {
      serviceId: 'Service_kl6xanx',
      templateId: 'template_ag69e2c',
      publicKey: 'hastGfwRELWtBigOW',
      initialized: false,
      label: 'account-2'
    },
    {
      serviceId: 'Service_2puxkph',
      templateId: 'template_7kjn382',
      publicKey: 'GUghH_Q3K2ucXgZD_',
      initialized: false,
      label: 'account-3'
    }
  ];

  // Legacy single-account fields kept for backward compatibility (not used directly anymore)
  private emailjsServiceId = 'service_28s246g';
  private emailjsTemplateId = 'template_pfgp3te';
  private emailjsPublicKey = '8aA0otiM0u1nUtf7q';
  private emailjsInitialized = false;

  // ============ DEDUPLICATION GUARD (NEW) ============
  // Prevents the same message from going out twice in quick succession.
  // Key = hash of (title + body + recipients). TTL = 60 seconds.
  private recentSends: Map<string, number> = new Map();
  private readonly DEDUP_TTL_MS = 60_000;

  // ============ BATCH LIMIT (NEW) ============
  // If email list is longer than this, randomly sample down to it.
  private readonly MAX_EMAILS_PER_SEND = 15;

  private constructor() {
    console.log('🔧 NotificationService initialized with direct OneSignal');
    // ============ EMAILJS INIT (NEW) ============
    // Note: emailjs is a global SDK — multiple init() calls overwrite.
    // We init with the first account's key globally, but per-send we
    // pass the chosen account's publicKey inside the options object
    // (emailjs.send supports a 4th arg = { publicKey }).
    try {
      emailjs.init(this.emailjsAccounts[0].publicKey);
      this.emailjsAccounts[0].initialized = true;
      this.emailjsInitialized = true;
      console.log('✅ EmailJS initialized (primary account)');
    } catch (e) {
      console.warn('⚠️ EmailJS init failed:', e);
      this.emailjsInitialized = false;
    }

    // Verify the other accounts have their keys (no init needed at construction;
    // we pass publicKey per send)
    for (let i = 1; i < this.emailjsAccounts.length; i++) {
      this.emailjsAccounts[i].initialized = !!this.emailjsAccounts[i].publicKey;
    }
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  // ============ HELPERS (NEW) ============

  /**
   * Build a stable key for deduplication.
   * Same title + body + sorted recipients => same key.
   */
  private buildDedupKey(title: string, body: string, recipients: string[]): string {
    const sorted = [...recipients].sort().join('|');
    return `${title}::${body}::${sorted}`;
  }

  /**
   * Returns true if this exact message was already sent within TTL.
   * Otherwise records it and returns false.
   */
  private isDuplicateAndRecord(title: string, body: string, recipients: string[]): boolean {
    const now = Date.now();

    // Clean expired entries first
    this.recentSends.forEach((ts, key) => {
      if (now - ts > this.DEDUP_TTL_MS) this.recentSends.delete(key);
    });

    const key = this.buildDedupKey(title, body, recipients);
    if (this.recentSends.has(key)) {
      return true;
    }
    this.recentSends.set(key, now);
    return false;
  }

  /**
   * Randomly sample down to max N if the list is longer.
   * Deduplicates + filters empties first.
   */
  private capEmailList(emails: string[]): string[] {
    // Remove empties + duplicates
    const cleaned = Array.from(
      new Set(emails.map(e => (e || '').trim()).filter(e => e.length > 0))
    );

    if (cleaned.length <= this.MAX_EMAILS_PER_SEND) return cleaned;

    // Fisher–Yates shuffle then slice
    const shuffled = [...cleaned];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, this.MAX_EMAILS_PER_SEND);
  }

  /**
   * Pick a random EmailJS account.
   */
  private pickRandomAccount(): EmailJsAccount {
    const usable = this.emailjsAccounts.filter(a => a.initialized && a.publicKey);
    if (usable.length === 0) return this.emailjsAccounts[0];
    return usable[Math.floor(Math.random() * usable.length)];
  }

  /**
   * Send one email via a specific EmailJS account.
   * Returns true on success.
   */
  private async sendViaAccount(
    account: EmailJsAccount,
    toEmail: string,
    request: NotificationRequest
  ): Promise<boolean> {
    try {
      await emailjs.send(
        account.serviceId,
        account.templateId,
        {
          title: request.title,
          message: request.body,
          to_email: toEmail,
          email: toEmail,
          redirect_url: request.redirect_url || '',
          notification_type: request.notification_type || 'system',
          year: new Date().getFullYear().toString()
        },
        { publicKey: account.publicKey } // per-send key override
      );
      return true;
    } catch (err) {
      console.warn(`⚠️ EmailJS ${account.label} failed for ${toEmail}:`, err);
      return false;
    }
  }

  /**
   * Send all recipients, first trying the chosen account,
   * and for any failed emails, retrying via the other accounts.
   */
  private async sendEmailsWithFailover(
    emails: string[],
    request: NotificationRequest
  ): Promise<{ successCount: number; usedAccounts: string[] }> {
    const usedAccounts: string[] = [];

    // 1. Primary account — random
    const primary = this.pickRandomAccount();
    usedAccounts.push(primary.label);

    const primaryResults = await Promise.allSettled(
      emails.map(toEmail => this.sendViaAccount(primary, toEmail, request))
    );

    // Collect emails that failed
    const failedEmails: string[] = [];
    primaryResults.forEach((res, idx) => {
      const ok = res.status === 'fulfilled' && res.value === true;
      if (!ok) failedEmails.push(emails[idx]);
    });

    let successCount = emails.length - failedEmails.length;
    console.log(`📮 EmailJS ${primary.label}: ${successCount}/${emails.length} sent`);

    if (failedEmails.length === 0) {
      return { successCount, usedAccounts };
    }

    // 2. Failover — try each other account for remaining emails
    const otherAccounts = this.emailjsAccounts.filter(
      a => a.label !== primary.label && a.initialized && a.publicKey
    );

    let remaining = failedEmails;
    for (const account of otherAccounts) {
      if (remaining.length === 0) break;
      usedAccounts.push(account.label);

      const results = await Promise.allSettled(
        remaining.map(toEmail => this.sendViaAccount(account, toEmail, request))
      );

      const stillFailed: string[] = [];
      results.forEach((res, idx) => {
        const ok = res.status === 'fulfilled' && res.value === true;
        if (ok) successCount++;
        else stillFailed.push(remaining[idx]);
      });

      console.log(
        `📮 EmailJS ${account.label} failover: ${remaining.length - stillFailed.length}/${remaining.length} sent`
      );
      remaining = stillFailed;
    }

    if (remaining.length > 0) {
      console.warn(`⚠️ ${remaining.length} email(s) still failed after all accounts`);
    }

    return { successCount, usedAccounts };
  }

  /**
   * Main method to send notifications
   * Push: Direct to OneSignal API
   * Email: Through backend
   */
  async sendNotification(request: NotificationRequest): Promise<NotificationResponse> {
    console.log('📨 NotificationService received request:', request);

    const startTime = Date.now();
    const channels: string[] = [];
    
    let pushResult: PushResult | undefined;
    let emailResult: EmailResult | undefined;

    try {
      // Validate request
      if (!request.title || !request.body) {
        throw new Error('Title and body are required');
      }

      // Extract target users
      const targetUserIds: string[] = [];
      if (request.target_user_ids && Array.isArray(request.target_user_ids)) {
        targetUserIds.push(...request.target_user_ids);
      } else if (request.target_user_id) {
        targetUserIds.push(request.target_user_id);
      }

      // Extract emails
      const emailList: string[] = [];
      if (request.email_list && Array.isArray(request.email_list)) {
        emailList.push(...request.email_list);
      } else if (request.email) {
        emailList.push(request.email);
      }

      if (targetUserIds.length === 0 && emailList.length === 0) {
        throw new Error('No recipients specified');
      }

      // ============ DEDUPLICATION GUARD (NEW) ============
      // Prevents the same message going out twice in quick succession.
      const allRecipients = [...targetUserIds, ...emailList];
      if (allRecipients.length > 0) {
        if (this.isDuplicateAndRecord(request.title, request.body, allRecipients)) {
          console.warn('🚫 Duplicate notification detected — skipping send');
          return {
            success: true,
            message: 'Duplicate notification skipped (already sent recently)',
            channels: ['deduplicated'],
            targetUsers: targetUserIds.length,
            emails: emailList.length,
            timestamp: new Date().toISOString()
          };
        }
      }

      // ============ SEND PUSH NOTIFICATIONS DIRECT TO ONESIGNAL ============
      if (targetUserIds.length > 0) {
        console.log(`📱 Sending push to ${targetUserIds.length} user(s) via direct OneSignal API`);
        
        try {
          // Prepare OneSignal payload exactly as in your working curl command
          const onesignalPayload = {
            app_id: this.onesignalAppId,
            include_aliases: {
              external_id: targetUserIds
            },
            target_channel: 'push',
            contents: {
              en: request.body
            },
            headings: {
              en: request.title
            },
            ...(request.redirect_url && { url: request.redirect_url }),
            ...(request.data && { data: request.data })
          };

          console.log('📤 OneSignal payload:', JSON.stringify(onesignalPayload, null, 2));

          // Call OneSignal API directly
          const pushResponse = await fetch('https://api.onesignal.com/notifications', {
            method: 'POST',
            headers: {
              'Authorization': `Key ${this.onesignalApiKey}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(onesignalPayload)
          });

          const responseData = await pushResponse.json();
          
          if (pushResponse.ok) {
            pushResult = {
              success: true,
              count: targetUserIds.length,
              response: responseData
            };
            channels.push('onesignal');
            console.log('✅ OneSignal push successful:', responseData);
          } else {
            pushResult = {
              success: false,
              count: 0,
              error: JSON.stringify(responseData),
              response: responseData
            };
            console.error('❌ OneSignal push failed:', responseData);
          }
        } catch (pushError: any) {
          console.error('❌ OneSignal push exception:', pushError);
          pushResult = {
            success: false,
            count: 0,
            error: pushError.message
          };
        }
      }

      // ============ SEND EMAIL NOTIFICATIONS ============
      // NEW ORDER: Try EmailJS first → fallback to backend
      if (emailList.length > 0) {
        console.log(`📧 Sending email to ${emailList.length} recipient(s)`);
        
        // ============ CAP EMAIL LIST (NEW) ============
        // If more than 15, randomly sample down to 15.
        const cappedEmails = this.capEmailList(emailList);
        if (cappedEmails.length < emailList.length) {
          console.log(
            `✂️ Email list capped: ${emailList.length} → ${cappedEmails.length} (max ${this.MAX_EMAILS_PER_SEND})`
          );
        }

        // ---------- ATTEMPT 1: EMAILJS (NEW) ----------
        let emailjsSuccess = false;
        if (cappedEmails.length > 0) {
          try {
            console.log('📮 Attempting EmailJS send (random account + failover)...');
            
            const { successCount, usedAccounts } = await this.sendEmailsWithFailover(
              cappedEmails,
              request
            );

            if (successCount > 0) {
              emailjsSuccess = true;
              emailResult = {
                success: true,
                count: successCount
              };
              channels.push('emailjs');
              console.log(
                `✅ EmailJS: ${successCount}/${cappedEmails.length} emails sent via ${usedAccounts.join(' → ')}`
              );
            } else {
              console.warn('⚠️ EmailJS: all sends failed across all accounts, falling back to backend');
            }
          } catch (emailjsError: any) {
            console.warn('⚠️ EmailJS exception, falling back to backend:', emailjsError);
          }
        }

        // ---------- ATTEMPT 2: BACKEND FALLBACK (existing code, unchanged) ----------
        if (!emailjsSuccess) {
          console.log('🔁 Falling back to backend email endpoint...');
          try {
            const emailResponse = await fetch(`${this.backendUrl}/api/notifications/create`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                title: request.title,
                body: request.body,
                receiver_ids: targetUserIds, 
                notification_type: request.notification_type || 'system',
                email_list: cappedEmails, // use capped list, not original
                redirect_url: request.redirect_url,
                data: request.data || {}
              })
            });

            if (emailResponse.ok) {
              const emailResultData = await emailResponse.json();
              emailResult = {
                success: true,
                count: cappedEmails.length
              };
              channels.push('email');
              console.log(`✅ Email: ${cappedEmails.length} emails sent successfully (backend)`);
            } else {
              const errorText = await emailResponse.text();
              emailResult = {
                success: false,
                count: 0,
                error: `HTTP ${emailResponse.status}: ${errorText}`
              };
              console.error('❌ Email failed (backend):', errorText);
            }
          } catch (emailError: any) {
            console.error('❌ Email error (backend):', emailError);
            emailResult = {
              success: false,
              count: 0,
              error: emailError.message
            };
          }
        }
      }

      // Build response
      const response: NotificationResponse = {
        success: channels.length > 0,
        message: channels.length > 0 
          ? `Notification sent via ${channels.join(', ')}` 
          : 'Failed to send via any channel',
        channels,
        targetUsers: targetUserIds.length,
        emails: emailList.length,
        timestamp: new Date().toISOString()
      };

      if (pushResult || emailResult) {
        response.results = {};
        if (pushResult) response.results.push = pushResult;
        if (emailResult) response.results.email = emailResult;
      }

      if (response.success) {
        response.notificationId = `notif_${Date.now()}`;
      }

      console.log(`✅ NotificationService completed in ${Date.now() - startTime}ms`, response);
      return response;

    } catch (error: any) {
      console.error('❌ NotificationService error:', error);
      
      return {
        success: false,
        message: error.message,
        channels: [],
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Helper to check OneSignal subscription status
   */
  async checkUserPushStatus(userId: string): Promise<boolean> {
    try {
      // You could implement this by calling OneSignal API to check subscription
      // For now, return true as we're sending directly
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Send a test notification (convenience method)
   */
  async sendTestNotification(
    title: string, 
    body: string, 
    targets: { users?: string[], emails?: string[] }
  ): Promise<NotificationResponse> {
    return this.sendNotification({
      title,
      body,
      target_user_ids: targets.users,
      email_list: targets.emails,
      notification_type: 'system',
      data: { test: true }
    });
  }
}

// Export singleton instance
export const notificationService = NotificationService.getInstance();